import React, { useState } from "react";
import { Info, Phone } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import defaultImg from "../Images/knofe.jpeg";
import img from "../Images/pc.png";
import img1 from "../Images/bf.png";
import img2 from "../Images/acuated.png";
import img3 from "../Images/rcb.png";
import img4 from "../Images/knifespec.png";
import img5 from "../Images/butterflyspec.png";
import img6 from "../Images/ballspec.png";
import img7 from "../Images/robotspec.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState('description');

  return (
    <article 
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6 lg:p-8"
      itemScope 
      itemType="https://schema.org/Product"
    >
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 text-gray-900" itemProp="name">
          {product.name}
        </h3>
      </header>
      
      <div className="bg-gray-100 h-48 sm:h-56 md:h-64 lg:h-72 mb-4 rounded-lg overflow-hidden">
        <img 
          src={product.mainImage}
          alt={`${product.name} - High-performance pneumatic valve by Gortherm Energy`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          itemProp="image"
          loading="lazy"
          width="600"
          height="400"
        />
      </div>

      <nav className="mb-4">
        <div className="flex border-b border-gray-200" role="tablist" aria-label="Product information">
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === 'description'
                ? 'border-b-2 border-teal-600 text-teal-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('description')}
            aria-selected={activeTab === 'description'}
            role="tab"
            aria-controls="description-panel"
          >
            Description
          </button>
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === 'specs'
                ? 'border-b-2 border-teal-600 text-teal-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('specs')}
            aria-selected={activeTab === 'specs'}
            role="tab"
            aria-controls="specs-panel"
          >
            <span className="hidden sm:inline">Technical Specifications</span>
            <span className="sm:hidden">Specs</span>
          </button>
        </div>
      </nav>

      <div className="mt-4" role="tabpanel" id={activeTab === 'description' ? 'description-panel' : 'specs-panel'}>
        {activeTab === 'description' ? (
          <p className="text-gray-700 text-justify text-sm sm:text-base leading-relaxed" itemProp="description">
            {product.description}
          </p>
        ) : (
          <div>
            {product.specImage && (
              <div className="bg-gray-100 h-48 sm:h-56 md:h-64 rounded-lg overflow-hidden mb-4">
                <img 
                  src={product.specImage}
                  alt={`${product.name} detailed technical specifications and measurements`}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
            )}
            <ul className="space-y-2 sm:space-y-3">
              {product.specifications.map((spec, index) => (
                <li key={index} className="flex items-start">
                  <Info className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-gray-700 text-sm sm:text-base">{spec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
};

const PneumaticValves = () => {
  const handleCall = () => {
    window.location.href = 'tel:+9779802781400';
  };

  const products = [
    {
      id: "actuated-knife-edge-gate",
      name: "Actuated Knife Edge Gate",
      mainImage: defaultImg,
      specImage: img4,
      description: `Actuated knife edge gate is an innovative gate valve design that features a sharpened blade that can smoothly cut through fluids to avoid the risk of clogging and minimize pressure drops in the system. As the leading actuated knife edge gate valve manufacturer, Gortherm Energy uses high-quality stainless steel to withstand demanding operating conditions, ensuring longevity and durability.`,
      specifications: [
        'Innovative sharpened blade design',
        'High-quality stainless steel construction',
        'Advanced actuation technology',
        'Pneumatic or electric mechanism options',
        'Precise and responsive control'
      ]
    },
    {
      id: "pneumatic-control-valves",
      name: "Pneumatic Control Valves",
      mainImage: img,
      specImage: "/ProdSpec/PC_Spec.jpg",
      description: `Gortherm Energy's Pneumatic Control Valves are designed to provide precise control over fluid flow in various industrial applications. These valves are made with high-quality materials such as stainless steel, carbon steel, and cast iron, ensuring a long-lasting and reliable performance even in harsh environments.`,
      specifications: [
        'Multiple material options available',
        'Diaphragm actuator system',
        'Precise flow control',
        'Suitable for harsh environments',
        'Cost-effective solution'
      ]
    },
    {
      id: "actuated-butterfly-valves",
      name: "Actuated Butterfly Valves",
      mainImage: img1,
      specImage: img5,
      description: `Actuated butterfly valves are a combination of advanced technology and precision engineering, ensuring optimal performance in challenging environments. It consists of a circular disc, a stem, and a valve body, where the disc rotates inside the valve body to regulate the flow of fluid through the pipeline.`,
      specifications: [
        'Electric or pneumatic actuation',
        'Corrosion resistant materials',
        'High pressure tolerance',
        'Extended service life',
        'Minimal maintenance requirements'
      ]
    },
    {
      id: "actuated-ball-valves",
      name: "Actuated Ball Valves",
      mainImage: img2,
      specImage: img6,
      description: `Actuated ball valves find common applications in controlling the flow of water, air, oil, and other compatible substances, providing both ON/OFF functionality and modulating control. With the integration of actuators, users gain the advantage of remote and automatic control.`,
      specifications: [
        'Remote and automatic control',
        'Various size options',
        'Multiple pressure ratings',
        'Easy installation',
        'Low maintenance requirements'
      ]
    },
    {
      id: "robotrol-pressure-control",
      name: "Robotrol Operated Pressure Control Valve",
      mainImage: img3,
      specImage: img7,
      description: `Gortherm Energy is a renowned manufacturer of highly efficient and innovative pressure control valves, including the advanced Robotrol-operated pressure control valve. This valve is designed to offer precise downstream pressure control and is highly in demand due to its accurate and reliable performance.`,
      specifications: [
        'Precise downstream pressure control',
        'Compact and robust design',
        'Pneumatically operated actuator',
        'High-precision control',
        'Suitable for harsh conditions'
      ]
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Pneumatic Valves & Control Solutions",
    "description": "Advanced pneumatic valves including actuated knife edge gates, butterfly valves, ball valves, and pressure control systems",
    "itemListElement": products.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name,
      "description": product.description,
      "image": product.mainImage,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Gortherm Energy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Pneumatic Valves & Actuated Control Solutions | Gortherm Energy</title>
        <meta name="description" content="Leading manufacturer of pneumatic valves including actuated knife edge gates, butterfly valves, ball valves, and Robotrol pressure control systems. High-quality industrial valve solutions for demanding applications." />
        <meta name="keywords" content="pneumatic valves, actuated knife edge gate, pneumatic control valves, actuated butterfly valves, actuated ball valves, robotrol pressure control valve, industrial valves, valve automation, flow control systems, pressure control valves Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Pneumatic Valves & Actuated Control Solutions | Gortherm Energy" />
        <meta property="og:description" content="Advanced pneumatic valve solutions for industrial applications. Expert manufacturing of actuated valves and control systems." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/pneumatic-valves" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pneumatic Valves & Actuated Control Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="High-performance pneumatic valves and actuated control systems for industrial processes." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/pneumatic-valves" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <header className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Pneumatic Valves & Control Solutions
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Discover our comprehensive range of pneumatic valves and solutions,
              engineered to meet the demanding requirements of modern industrial processes.
            </p>
          </header>

          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </section>

          <aside className="mt-12 sm:mt-16 lg:mt-20 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Need Expert Guidance?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 px-4">
                Our technical experts are ready to help you select the right pneumatic valve 
                for your specific application requirements.
              </p>
              <button
                onClick={handleCall}
                className="bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold"
                aria-label="Request consultation - Call Gortherm Energy"
              >
                <span>Request Consultation</span>
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
              </button>
              
            </div>
          </aside>

          {/* Additional SEO Content Section */}
          <section className="mt-12 sm:mt-16 bg-white rounded-xl p-6 sm:p-8 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
              Why Choose Gortherm Energy Pneumatic Valves?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-gray-700">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Superior Quality</h3>
                <p className="text-sm sm:text-base">
                  Our pneumatic valves are manufactured using high-grade materials including stainless steel, 
                  carbon steel, and specialized alloys to ensure maximum durability and performance.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Advanced Automation</h3>
                <p className="text-sm sm:text-base">
                  Equipped with state-of-the-art pneumatic and electric actuators for precise control, 
                  remote operation, and seamless integration with industrial control systems.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Versatile Applications</h3>
                <p className="text-sm sm:text-base">
                  Suitable for water treatment, oil and gas, chemical processing, HVAC systems, 
                  and various industrial processes requiring reliable flow control.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Expert Support</h3>
                <p className="text-sm sm:text-base">
                  Comprehensive technical support, custom sizing, installation guidance, 
                  and maintenance services to ensure optimal valve performance.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default PneumaticValves;